import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import RouteWrapper from './RouteWrapper';
import Error from '@/components/Error';
import { InterviewProvider } from '@/provider/InterviewProvider';

import {
  CANDIDATE_PROFILE,
  FORGOT_PASSWORD,
  ICONLIST,
  LOGIN,
  SIGNUP,
  ADD_CAMPAIGN,
  EDIT_CAMPAIGN,
  CAMPAIGN_DETAIL,
  ADD_CANDIDATE_CAMPAIGN,
  INSTANT_INTERVIEW_SETUP,
  LIVE_INTERVIEW,
  USERS_LIST,
  PROFILE,
  INTEGRATIONS,
  ATS_AUTH_GUIDE,
  ZOOM_INTEGRATION_GUIDE,
  CONSENT,
  BRANDING_CONSENT,
  DASHBOARD_OVERVIEW,
  CAMPAIGN_LIST,
  SEARCH,
  GO,
  NOT_FOUND,
  QUESTION_TEMPLATES,
  QUESTION_TEMPLATE_NEW,
  QUESTION_TEMPLATE_EDIT,
} from './routes';
import { AtsProvider } from '@/provider/AtsProvider';
import { CalendarProvider } from '@/provider/CalendarProvider';

const Dashboard = lazy(() => import('../core/protected/Dashboard'));
const BreadcrumbLayout = lazy(() => import('@/core/protected/BreadcrumbLayout'));
const Interview = lazy(() => import('../core/protected/Interview'));
const OverviewPage = lazy(() => import('@/core/protected/Overview/OverviewPage'));
const AddCampaign = lazy(() => import('@/core/protected/JobCampaigns/Pages/CampaignList/AddCampaign'));
const AddCandidate = lazy(() => import('@/core/protected/JobCampaigns/Pages/CandidateList/AddCandidate'));
const CandidateInfo = lazy(() => import('@/core/protected/Candidate/CandidateInfo'));
const Icons = lazy(() => import('@/core/public/IconList'));
const CandidateList = lazy(() => import('@/core/protected/JobCampaigns/Pages/CandidateList'));
const LiveInterview = lazy(() => import('@/core/protected/LiveInterview'));
const UsersPage = lazy(() => import('@/core/protected/Users/UsersPage'));
const ProfilePage = lazy(() => import('@/core/protected/Profile/ProfilePage'));
const Integrations = lazy(() => import('@/core/protected/Integrations'));
const AtsAuthGuidePage = lazy(() => import('@/core/protected/Integrations/Ats/AtsAuthGuidePage'));
const ZoomIntegrationGuide = lazy(() => import('@/core/public/ZoomIntegrationGuide'));
const Consent = lazy(() => import('@/core/public/Consent'));
const BrandingConsentPage = lazy(() => import('@/core/protected/ConsentSettings'));
const OwnerRoute = lazy(() => import('./guards/OwnerRoute'));
const GoRoute = lazy(() => import('./GoRoute'));
const GlobalSearch = lazy(() => import('@/core/protected/Search/GlobalSearch'));
const QuestionTemplates = lazy(() => import('@/core/protected/QuestionTemplates'));
const QuestionTemplateEditor = lazy(() => import('@/core/protected/QuestionTemplates/TemplateEditor'));

export type CustomRouteObject = RouteObject & {
  title?: string;
  children?: CustomRouteObject[];
};

const protectedRoutes = [
  {
    path: LOGIN,
    element: <Navigate to="/" replace />,
  },
  {
    path: SIGNUP,
    element: <Navigate to="/" replace />,
  },
  {
    path: FORGOT_PASSWORD,
    element: <Navigate to="/" replace />,
  },
  {
    path: GO,
    title: 'Redirecting',
    element: (
      <RouteWrapper>
        <GoRoute />
      </RouteWrapper>
    ),
  },
  {
    path: ATS_AUTH_GUIDE,
    title: 'ATS Authorization Guide',
    element: (
      <RouteWrapper>
        <AtsAuthGuidePage />
      </RouteWrapper>
    ),
  },
  {
    path: ZOOM_INTEGRATION_GUIDE,
    title: 'Connecting Zoom',
    element: (
      <RouteWrapper>
        <ZoomIntegrationGuide />
      </RouteWrapper>
    ),
  },
  {
    // Consent page also reachable when a logged-in recruiter previews the candidate link. Read-only:
    // without `preview` a recruiter could open a candidate's link and record their consent for them.
    path: CONSENT,
    element: (
      <RouteWrapper>
        <Consent preview />
      </RouteWrapper>
    ),
  },
  {
    path: NOT_FOUND,
    title: 'Page Not Found',
    element: (
      <RouteWrapper>
        <Error statusCode={404} title="Page Not Found" message="The page you are looking for does not exist." />
      </RouteWrapper>
    ),
  },
  {
    path: '/',
    element: (
      <RouteWrapper>
        <AtsProvider>
          <Dashboard></Dashboard>
        </AtsProvider>
      </RouteWrapper>
    ),
    errorElement: (
      <Error statusCode={404} title="Page Not Found" message="The page you are looking for does not exist." />
    ),
    children: [
      {
        path: DASHBOARD_OVERVIEW,
        title: 'Dashboard',
        element: <OverviewPage />,
      },
      {
        path: CAMPAIGN_LIST,
        title: 'Interview List',
        element: <Interview />,
      },
      {
        path: SEARCH,
        title: 'Search',
        element: <GlobalSearch />,
      },
      {
        path: ADD_CANDIDATE_CAMPAIGN,
        title: 'Add new candidate',
        element: <AddCandidate />,
      },

      {
        path: ICONLIST,
        title: 'Icon List',
        element: <Icons />,
      },

      {
        path: USERS_LIST,
        title: 'Manage Team',
        element: <UsersPage />,
      },
      {
        path: PROFILE,
        title: 'Profile',
        element: <ProfilePage />,
      },
      {
        path: INTEGRATIONS,
        title: 'Integrations',
        element: (
          <CalendarProvider>
            <Integrations />
          </CalendarProvider>
        ),
      },
      {
        path: QUESTION_TEMPLATES,
        title: 'Question templates',
        element: <QuestionTemplates />,
      },
      // Declared before the :templateId route so "new" is not read as an id.
      {
        path: QUESTION_TEMPLATE_NEW,
        title: 'New template',
        element: <QuestionTemplateEditor />,
      },
      {
        path: QUESTION_TEMPLATE_EDIT,
        title: 'Edit template',
        element: <QuestionTemplateEditor />,
      },
    ],
  },
  {
    element: (
      <RouteWrapper>
        <AtsProvider>
          <BreadcrumbLayout />
        </AtsProvider>
      </RouteWrapper>
    ),
    errorElement: (
      <Error statusCode={404} title="Page Not Found" message="The page you are looking for does not exist." />
    ),
    children: [
      {
        path: CAMPAIGN_DETAIL,
        title: 'Campaign Detail',
        element: (
          <CalendarProvider>
            <CandidateList />
          </CalendarProvider>
        ),
      },
      {
        path: ADD_CAMPAIGN,
        title: 'Add new campaign',
        element: <AddCampaign />,
      },
      {
        path: EDIT_CAMPAIGN,
        title: 'Edit campaign',
        element: <AddCampaign />,
      },
      {
        path: INSTANT_INTERVIEW_SETUP,
        title: 'Instant interview setup',
        element: <AddCampaign />,
      },
      {
        path: CANDIDATE_PROFILE,
        title: 'Candidate Profile',
        element: (
          <CalendarProvider>
            <InterviewProvider>
              <CandidateInfo />
            </InterviewProvider>
          </CalendarProvider>
        ),
      },
      {
        path: BRANDING_CONSENT,
        title: 'Consent Page',
        element: (
          <OwnerRoute>
            <BrandingConsentPage />
          </OwnerRoute>
        ),
      },
      {
        path: LIVE_INTERVIEW,
        title: 'Live Interview',
        element: (
          <InterviewProvider>
            <LiveInterview />
          </InterviewProvider>
        ),
      },
    ],
  },
];

export default protectedRoutes as CustomRouteObject[];
